/**
 * "Inspect" for a pe3d data slot: the slot's JSON — the game's own, a shipped
 * one or a library file — pretty-printed and read-only, with a copy button.
 * A file that is not valid JSON is shown as it is, with the parse error.
 */

import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CheckIcon from "@material-ui/icons/Check";
import { CONTROL_BG, CONTROL_BORDER, FIELD_LABEL } from "../snapshotEditor/panelPalette";
import { describeJson } from "../../modules/pe3dLibrary";
import { DIALOG_PAPER_PROPS, DIALOG_TEXT, MUTED, OK_TEXT, OUTLINED_BUTTON_STYLE } from "./pe3dPanelKit";

const fileName = (path) => String(path || "").split("/").pop();

/**
 * @param props.open
 * @param props.onClose
 * @param props.title   the slot's label
 * @param props.load    () => Promise<{ url, file } | null> — the slot's file
 */
export default function DialogPe3dDataInspect({ open, onClose, title, load }) {
  const [state, setState] = useState("loading"); // loading | ready | error
  const [file, setFile] = useState("");
  const [shown, setShown] = useState(null); // describeJson()
  const [error, setError] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState("loading");
    setShown(null);
    setError("");
    setIsCopied(false);
    (async () => {
      try {
        const source = await load();
        if (!source) throw new Error("This slot's file could not be read.");
        const text = await (await fetch(source.url)).text();
        if (cancelled) return;
        setFile(source.file);
        setShown(describeJson(text));
        setState("ready");
      } catch (err) {
        if (cancelled) return;
        setError(err && err.message ? err.message : String(err));
        setState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shown.text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    } catch (_err) {
      setError("Could not copy — select the text and copy it by hand.");
    }
  };

  return (
    <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth PaperProps={DIALOG_PAPER_PROPS}>
      <DialogTitle>
        {title}
        {file && <span style={{ fontSize: 13, color: MUTED, marginLeft: 8 }}>{fileName(file)}</span>}
      </DialogTitle>
      <DialogContent>
        {state === "loading" && (
          <div style={DIALOG_TEXT.busy}>
            <CircularProgress size={18} /> Reading the file…
          </div>
        )}
        {state === "error" && <Typography style={DIALOG_TEXT.failure}>Could not show the file — {error}</Typography>}
        {state === "ready" && shown && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: shown.error ? "#ef9a9a" : FIELD_LABEL, flex: 1 }}>
                {shown.error ? `Not valid JSON — ${shown.error}` : `JSON · ${shown.summary}`}
              </span>
              <Button
                size="small"
                variant="outlined"
                startIcon={isCopied ? <CheckIcon fontSize="small" style={{ color: OK_TEXT }} /> : <FileCopyIcon fontSize="small" />}
                onClick={copy}
                style={OUTLINED_BUTTON_STYLE}
              >
                {isCopied ? "Copied" : "Copy"}
              </Button>
            </div>
            <pre
              className="proto-dark-scroll"
              style={{
                margin: 0,
                maxHeight: "60vh",
                overflow: "auto",
                padding: "10px 12px",
                backgroundColor: CONTROL_BG,
                border: `1px solid ${CONTROL_BORDER}`,
                borderRadius: 4,
                fontSize: 12,
                lineHeight: 1.5,
                color: "#dfe3ea",
                fontFamily: "monospace",
                whiteSpace: "pre",
                userSelect: "text",
              }}
            >
              {shown.text}
            </pre>
            {error && <Typography style={DIALOG_TEXT.failure}>{error}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
